import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MdAccessTime,
  MdAttachFile,
  MdBookmarkAdd,
  MdCalendarToday,
  MdCancel,
  MdCategory,
  MdCheckCircle,
  MdDelete,
  MdDownload,
  MdEdit,
  MdMarkEmailUnread,
  MdPersonAdd,
  MdPlace,
  MdRepeat,
  MdSchedule,
} from "react-icons/md";
import AtriumApi, { getApiErrorMessage } from "api";
import Avatar from "components/avatar";
import Badge from "components/badge";
import Button from "components/button";
import Dialog from "components/dialog";
import { useConfirm } from "components/dialog/ConfirmProvider";
import SideDrawer from "components/drawer/SideDrawer";
import { InputField } from "components/fields";
import { EmptyState } from "components/states";
import notify from "lib/notify";
import { EMPTY, formatDate, formatTime } from "utils/format";
import { getServiceColors, getConfirmationStatus } from "./utils/serviceColors";

const TEMPLATE_FORM_ID = "save-template-form";

// "18:30:00" → "6:30 PM"
const formatClock = (time) => (time ? formatTime(`2000-01-01T${time}`) : EMPTY);

const USER_STATES = {
  confirmed: { tone: "success", label: "Confirmed", icon: MdCheckCircle },
  declined: { tone: "danger", label: "Declined", icon: MdCancel },
};

const DetailRow = ({ icon: Icon, label, iconClassName = "bg-surface text-fg-muted", children }) => (
  <div className="flex items-center gap-3">
    <span
      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-control ${iconClassName}`}
    >
      <Icon aria-hidden="true" className="h-5 w-5" />
    </span>
    <div className="min-w-0 flex-1">
      <p className="text-xs text-fg-muted">{label}</p>
      <p className="truncate text-sm font-semibold text-fg">{children}</p>
    </div>
  </div>
);

const ServiceDetails = ({ isOpen, onClose, service, onDelete, getServices, type }) => {
  const [tempName, setTempName] = useState(service?.tempName || "");
  const [loading, setLoading] = useState(false);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const confirm = useConfirm();
  const navigate = useNavigate();
  const colors = getServiceColors(service?.type);
  const status = getConfirmationStatus(service?.users);
  const attachments = service?.attachments || [];
  const users = service?.users || [];
  const pendingCount = users.filter((u) => u.state === "na").length;

  const API_HOST = process.env.REACT_APP_API_HOST || "http://localhost:8080";

  const editService = (tab) => {
    navigate("/admin/service-scheduling/services/" + service?.id + "/edit?tab=" + tab);
  };

  const deleteService = async () => {
    const confirmed = await confirm({
      title: `Delete "${service?.name}"?`,
      message: "This service will be permanently deleted.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!confirmed) return;

    try {
      const response = await AtriumApi.delete(`/v1/services/${service?.id}`);
      onDelete?.(service?.id);
      if (response) {
        notify.success("Service deleted");
        getServices();
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not delete service"));
    }
  };

  const saveAsTemplate = async (e) => {
    e.preventDefault();
    if (!type || !tempName) return;
    try {
      setLoading(true);
      const response = await AtriumApi.put(`/v1/services/${service?.id}`, {
        isTemplate: true,
        tempName,
      });
      if (response) {
        notify.success("Template saved");
        getServices();
        setShowTemplateForm(false);
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not save template"));
    } finally {
      setLoading(false);
    }
  };

  const sendReminders = async () => {
    try {
      setLoading(true);
      const response = await AtriumApi.post(`/v1/services/${service?.id}/send-reminders`);
      if (response) {
        const count = response?.data?.sentCount || 0;
        notify.success(
          count > 0
            ? `Sent ${count} reminder${count === 1 ? "" : "s"}`
            : "All users have already responded"
        );
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not send reminders"));
    } finally {
      setLoading(false);
    }
  };

  const downloadAttachment = (attachment) => {
    const link = document.createElement("a");
    link.href = attachment.url.startsWith("http") ? attachment.url : `${API_HOST}${attachment.url}`;
    link.download = attachment.filename || "download";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const footer = (
    <>
      <Button
        variant="danger"
        leftIcon={<MdDelete aria-hidden="true" />}
        onClick={deleteService}
        className="mr-auto"
      >
        Delete
      </Button>
      <Button
        variant="secondary"
        leftIcon={<MdBookmarkAdd aria-hidden="true" />}
        onClick={() => setShowTemplateForm(true)}
      >
        Save as Template
      </Button>
      {pendingCount > 0 && (
        <Button
          variant="secondary"
          leftIcon={<MdMarkEmailUnread aria-hidden="true" />}
          onClick={sendReminders}
          loading={loading && !showTemplateForm}
        >
          Send Reminders ({pendingCount})
        </Button>
      )}
    </>
  );

  return (
    <SideDrawer isOpen={isOpen} onClose={onClose} title={service?.name} size="lg" footer={footer}>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="secondary"
          leftIcon={<MdEdit aria-hidden="true" />}
          onClick={() => editService("details")}
        >
          Edit
        </Button>
        <Button
          variant="secondary"
          leftIcon={<MdPersonAdd aria-hidden="true" />}
          onClick={() => editService("assign")}
        >
          Assign People
        </Button>
      </div>

      <div className="mt-6 flex flex-col gap-3 rounded-card bg-surface-raised p-4">
        <DetailRow icon={MdCategory} label="Service Type" iconClassName={`${colors.bg} ${colors.text}`}>
          {service?.type || EMPTY}
        </DetailRow>
        {service?.address && (
          <DetailRow icon={MdPlace} label="Location">
            {service.address}
          </DetailRow>
        )}
        <DetailRow icon={MdCalendarToday} label="Date">
          {formatDate(service?.date)}
        </DetailRow>
        <DetailRow icon={MdAccessTime} label="Time">
          {formatClock(service?.startAt)} - {formatClock(service?.endAt)}
        </DetailRow>
        <DetailRow icon={MdRepeat} label="Schedule">
          <span className="capitalize">{service?.schedule || "One-time"}</span>
        </DetailRow>
        {attachments.length > 0 && (
          <DetailRow icon={MdAttachFile} label="Attachments">
            {attachments.length} {attachments.length === 1 ? "file" : "files"} attached
          </DetailRow>
        )}
      </div>

      {attachments.length > 0 && (
        <section className="mt-6">
          <h3 className="mb-3 text-heading">Attachments</h3>
          <ul className="flex flex-col gap-2">
            {attachments.map((attachment, index) => (
              <li key={index}>
                <button
                  type="button"
                  onClick={() => downloadAttachment(attachment)}
                  className="focus-ring group flex w-full items-center gap-3 rounded-control bg-surface-raised p-3 text-left transition-colors hover:bg-surface-subtle"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-control bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200">
                    <MdAttachFile aria-hidden="true" className="h-5 w-5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-fg">
                      {attachment.filename}
                    </span>
                    <span className="block text-xs text-fg-muted">
                      {attachment.size
                        ? `${(attachment.size / 1024 / 1024).toFixed(2)} MB`
                        : "Unknown size"}
                      {attachment.uploadedAt && ` · ${formatDate(attachment.uploadedAt)}`}
                    </span>
                  </span>
                  <MdDownload
                    aria-hidden="true"
                    className="h-4 w-4 shrink-0 text-fg-muted transition-colors group-hover:text-fg"
                  />
                  <span className="sr-only">Download</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="mt-6">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-heading">Assigned Team</h3>
          {users.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              {status.confirmed > 0 && (
                <Badge tone="success">
                  <MdCheckCircle aria-hidden="true" />
                  {status.confirmed} Confirmed
                </Badge>
              )}
              {status.pending > 0 && (
                <Badge tone="warning">
                  <MdSchedule aria-hidden="true" />
                  {status.pending} Pending
                </Badge>
              )}
              {status.declined > 0 && (
                <Badge tone="danger">
                  <MdCancel aria-hidden="true" />
                  {status.declined} Declined
                </Badge>
              )}
            </div>
          )}
        </div>

        {users.length === 0 ? (
          <EmptyState variant="inline" title="No one assigned to this service yet" />
        ) : (
          <ul className="flex flex-col gap-2">
            {users.map((user) => {
              const state = USER_STATES[user?.state];
              return (
                <li
                  key={`user${user?.id}`}
                  className="flex items-center gap-3 rounded-control bg-surface-raised p-2"
                >
                  <Avatar user={user} size="sm" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-fg">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-xs capitalize text-fg-muted">{user?.role}</p>
                  </div>
                  {state && (
                    <Badge tone={state.tone} size="sm">
                      <state.icon aria-hidden="true" />
                      {state.label}
                    </Badge>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <Dialog
        isOpen={showTemplateForm}
        onClose={() => setShowTemplateForm(false)}
        title="Save as Template"
        description="Reuse this service's details when creating new services."
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowTemplateForm(false)} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" form={TEMPLATE_FORM_ID} loading={loading} disabled={!tempName}>
              Save Template
            </Button>
          </>
        }
      >
        <form id={TEMPLATE_FORM_ID} onSubmit={saveAsTemplate}>
          <InputField
            label="Template Name"
            required
            placeholder="Add template name"
            value={tempName}
            onChange={(e) => setTempName(e.target.value)}
          />
        </form>
      </Dialog>
    </SideDrawer>
  );
};

export default ServiceDetails;
